import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * PageTopBar — 全站頁首列(R292.11;1:1 對齊 D3 `.topbar`)
 * -----------------------------------------------------------------------------
 * owner 2026-08-30:「我發現頁首的編排方式都有大改…例如專案 首頁 成效報表等等,
 * 都是在最上面那邊有排版的問題。」
 *
 * D3 每一頁的頂端都是同一條:
 *
 *   ┌ 標題 ─│─ 控制項(篩選 / 搜尋 / 排序)… ─── 右側動作 ┐   高 56、下緣一條線
 *
 * 幾何照 `brand.css:.topbar`:`height:56px; padding:0 24px; gap:12px; border-bottom`,
 * 標題 `17px/900`(`.page-title`),分隔線 `1px × 20px`(`.tb-div`)。
 *
 * ⚠️ **分隔線只在有控制項或動作時畫** —— D3 的「首頁」只有標題、沒有那條線。
 * ⚠️ 這一列**不放副標**:D3 的說明句(例:品牌規範的「品牌規範屬於 X 工作區…」)
 * 是內容區第一塊 `.pg-intro`,不是頁首的一部分。把副標塞回頁首會讓那條線長回
 * 兩層高、各頁又各自為政。
 *
 * ## R377 — `pinned`(owner 2026-09-24:「成效報表最上面的時間查詢等等進行固定,
 * 隨著用戶移動而鎖住」)
 *
 * ⚠️ **不可以用 `position: sticky`**(R371 紅線):AppShell 一般頁的 main 是
 * `overflow-y: auto` 卻不限高 —— 被內容撐到文件高、自己永遠不捲,真正在捲的是
 * window,sticky 黏到 main = 無效而且零徵兆。判準與 R371 匯入列同款:
 * `position: fixed` + `left: var(--hai-sidebar-w)`(側欄收合/展開跟著走,R258)。
 * fixed 脫離文件流 → 由**佔位 spacer** 撐住原本的高度,不讓內容跳上來被蓋;bar 有
 * `flexWrap`(窄視窗控制項換行)高度非定值 → ResizeObserver 同步實高(callback ref,
 * R352:量晚 render 的元素不可用 RefObject + useEffect)。z 低於側欄(z60)。
 * opt-in:預設不釘,既有頁面零行為變更。
 */
export function PageTopBar({
  title,
  children,
  actions,
  slotId,
  pinned = false,
  'data-testid': testId,
}: {
  title: string;
  /** 標題右邊、緊接分隔線的控制項(篩選 / 搜尋 / 排序)。 */
  children?: ReactNode;
  /** 靠右的動作鈕(建立 / 匯入 / 檢視切換)。 */
  actions?: ReactNode;
  /**
   * R292.12 — 給「篩選狀態不在頁面層」的頁面用的**掛載點**(素材庫:chips 與搜尋的
   * state 住在 `LibraryItemsSection` 裡)。給了 id 就在分隔線後面留一個空容器,
   * 由擁有 state 的元件 `createPortal` 進來 —— 比把整包篩選 state 往上搬安全得多。
   */
  slotId?: string;
  /** R377 — 捲動時釘在視窗頂(fixed;見檔頭)。預設 false = 既有行為。 */
  pinned?: boolean;
  'data-testid'?: string;
}) {
  const hasRail = !!children || !!actions || !!slotId;
  // R377 — 佔位高:跟著 bar 實高走(flexWrap 換行時會比 56 高)。ResizeObserver 由
  // callback ref 掛(元素在、observer 才在;R352 —— RefObject + useEffect([ref]) 對
  // 晚 render 的元素不重跑)。jsdom 沒有 ResizeObserver → 退 minHeight 56(測試鎖機制,
  // 真的「黏不黏」上瀏覽器看,R371 同款侷限)。
  const [spacerH, setSpacerH] = useState(56);
  const roRef = useRef<ResizeObserver | null>(null);
  const barRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setSpacerH(el.offsetHeight));
    ro.observe(el);
    roRef.current = ro;
    setSpacerH(el.offsetHeight);
  }, []);

  const bar = (
    <div
      ref={pinned ? barRef : undefined}
      style={pinned ? { ...barStyle, ...pinnedBarStyle } : barStyle}
      data-testid={testId ?? 'page-top-bar'}
    >
      <h1 style={titleStyle}>{title}</h1>
      {hasRail && <span style={dividerStyle} aria-hidden data-testid="page-top-bar-divider" />}
      {children}
      {slotId && <span id={slotId} style={slotStyle} data-testid={`${slotId}-slot`} />}
      <span style={{ flex: 1 }} />
      {actions}
    </div>
  );
  if (!pinned) return bar;
  return (
    <>
      {bar}
      {/* fixed 脫離文件流 —— spacer 撐住原高,內容不會跳上來被蓋。 */}
      <div style={{ height: spacerH }} aria-hidden data-testid="page-top-bar-spacer" />
    </>
  );
}

const barStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 56,
  padding: '0 24px',
  background: 'var(--ink-900)',
  borderBottom: '1px solid var(--hairline)',
  flexWrap: 'wrap',
};

/**
 * R377 — 釘住模式的差分(疊在 barStyle 上)。left 吃側欄寬變數(收合 64 / 展開 216,
 * 寫死會被側欄吃掉左半,R258);z 低於側欄的 60、高於頁內容與表格 sticky 表頭。
 */
const pinnedBarStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 'var(--hai-sidebar-w, 0px)',
  right: 0,
  zIndex: 40,
};

/** D3 `.page-title`:17px / 900 / 不換行。 */
const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 17,
  fontWeight: 900,
  letterSpacing: '-0.01em',
  color: 'var(--text-strong)',
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
};

/** portal 掛載點:自己不佔版面,由portal進來的內容決定。 */
const slotStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };

/** D3 `.tb-div`:1×20 的細線。 */
const dividerStyle: CSSProperties = {
  width: 1,
  height: 20,
  background: 'var(--hairline-strong)',
  flex: '0 0 1px',
};
